// The agent's reply as it is being written. The finished turn always arrives
// through the changelog like everything else; this is only what makes the wait
// readable, so it is deliberately in memory and thrown away when the turn lands.
//
// Factory sessions stream the same way, by name: each line of the conversation
// (the agent's words, a tool it ran) and each phase change, relayed from the
// factory. The thread shows the stored messages and then any live line with a
// newer seq; once the mirror carries a line it is dropped from here.
import { create } from 'zustand'
import type { AgentNote } from '@/db/types'

export interface LiveLine {
  seq: number
  at?: string
  role: string
  text: string
  pictures?: string[]
}

/** Most live lines kept per session (a long turn's tool lines are not worth more). */
const MAX_LIVE = 400

interface StreamState {
  /** session id → the text so far */
  text: Record<string, string>
  /** session id → what it is doing right now */
  step: Record<string, string>
  /** sessions whose latest message was sent from this device: the one that acts on the reply */
  sentHere: Record<string, true>
  /** factory session name → lines not yet in its mirror */
  runLines: Record<string, LiveLine[]>
  /** factory session name → its phase as last streamed, and when (ms) */
  runPhase: Record<string, { phase: string; at: number }>
  markSent(session: string): void
  note(n: AgentNote): void
  clear(session: string): void
  /** drop a factory session's live lines up to the seq its mirror now carries */
  settleRun(name: string, upTo: number): void
}

export const useStream = create<StreamState>((set) => ({
  text: {},
  step: {},
  sentHere: {},
  runLines: {},
  runPhase: {},
  markSent: (session) => set((s) => ({ sentHere: { ...s.sentHere, [session]: true } })),
  note: (n) =>
    set((s) => {
      if (n.run) {
        const { name, entry, phase } = n.run
        const out: Partial<StreamState> = {}
        if (entry) {
          const have = s.runLines[name] ?? []
          if (!have.some((l) => l.seq === entry.seq))
            out.runLines = { ...s.runLines, [name]: [...have, entry].slice(-MAX_LIVE) }
        }
        if (phase) out.runPhase = { ...s.runPhase, [name]: { phase, at: Date.now() } }
        return out
      }
      // "done" arrives a beat before the stored turn does. Clearing here would
      // flash the last step line in that gap; the view clears the streamed copy
      // itself once the session settles, so the words stay put until then.
      if (n.done) return s
      return {
        text: n.delta ? { ...s.text, [n.session]: (s.text[n.session] ?? '') + n.delta } : s.text,
        step: n.step !== undefined ? { ...s.step, [n.session]: n.step } : s.step,
      }
    }),
  clear: (session) =>
    set((s) => {
      const text = { ...s.text }
      const step = { ...s.step }
      delete text[session]
      delete step[session]
      return { text, step }
    }),
  settleRun: (name, upTo) =>
    set((s) => {
      const have = s.runLines[name]
      if (!have?.length || !have.some((l) => l.seq <= upTo)) return s
      return { runLines: { ...s.runLines, [name]: have.filter((l) => l.seq > upTo) } }
    }),
}))
